import { ENDPOINTS } from "@/api/routing";
import { getAxiosConfig } from "@/services/axiosConfig";
import { Data, type WithData } from "@/types/all.types";
import { IResponseFromQrApi } from "@/types/response.types";
import {
  GenericTypesUserManagement,
  IUserLogin,
  IUserManagement,
  IUserRegister,
  NewGenericAccount,
  type IChurnkeyTokenData,
} from "@/types/user.types";

const user = {
  postRegister: async (params: any) => {
    const res = await getAxiosConfig().post<IUserRegister | any>(
      ENDPOINTS.REGISTER(params?.via_signature),
      params,
    );
    return res.data;
  },

  postRegisterCheck: async ({ email }: { email: string }) => {
    const res = await getAxiosConfig().post<{ valid: boolean }>(ENDPOINTS.CHECK_REGISTER(), {
      email,
    });
    return res.data;
  },
  postLogout: async () => {
    const res = await getAxiosConfig().post<IUserRegister>(ENDPOINTS.LOGOUT());
    return res.data;
  },
  postSendResetNotification: async (params: { email: string }) => {
    const res = await getAxiosConfig().post(ENDPOINTS.RESET_NOTIFICATION(), params);
    return res.data;
  },
  postResetPasswordAccordingToken: async (params: any) => {
    const res = await getAxiosConfig().post<IUserLogin>(ENDPOINTS.RESET_PASSWORD(), params);
    return res.data;
  },
  postResendVerifyEmail: async () => {
    const res = await getAxiosConfig().post(ENDPOINTS.VERIFY_EMAIL());
    return res.data;
  },
  postLogin: async (params: any) => {
    const res = await getAxiosConfig().post<IUserLogin>(
      ENDPOINTS.LOGIN(params?.via_signature),
      params,
    );
    return res.data;
  },
  closeAccount: async (params: { delete_account: boolean; message: string }) => {
    const res = await getAxiosConfig().post(ENDPOINTS.DELETE_PROFILE(), params);
    return res.data;
  },

  closeAccountCheck: async () => {
    const res = await getAxiosConfig().post(ENDPOINTS.DELETE_PROFILE(), {
      check: 1,
    });
    return res.data;
  },
  updatePreferences: async (params: { timezone: string }) => {
    const res = await getAxiosConfig().put(ENDPOINTS.UPDATE_PREFERENCES(), params);
    return res.data;
  },
  accountNewShow: async (withQrPermissions?: boolean) => {
    let reqUrl = `user/profile/show?with=usage,sidebar-message,permissions`;
    if (withQrPermissions) reqUrl = reqUrl + `&permissions=generate,generateDynamic,generateStatic`;
    const res = await getAxiosConfig().get<NewGenericAccount>(reqUrl);
    return res.data;
  },
  accountBilling: async () => {
    const res = await getAxiosConfig().get(ENDPOINTS.GET_BILLING());
    return res.data;
  },
  updateAccount: async (params: any) => {
    const res = await getAxiosConfig().put(ENDPOINTS.UPDATE_ACCOUNT(), params);
    return res.data;
  },
  changePassword: async (params: any) => {
    const res = await getAxiosConfig().put(ENDPOINTS.CHANGE_PASSWORD(), params);
    return res.data;
  },
  changeEmail: async (params: any) => {
    const res = await getAxiosConfig().put(ENDPOINTS.CHANGE_EMAIL(), params);
    return res.data;
  },
  changeAddress: async (params: any) => {
    const res = await getAxiosConfig().post(ENDPOINTS.GET_BILLING(), params);
    return res.data;
  },
  getUserManagment: async () => {
    const res = await getAxiosConfig().get<IUserManagement<GenericTypesUserManagement[]>>(
      ENDPOINTS.GET_USER_MENAGEMENT(),
    );
    return res.data;
  },
  postUserManagment: async (params: { email: string; role: number }) => {
    const res = await getAxiosConfig().post<IUserManagement<GenericTypesUserManagement[]>>(
      ENDPOINTS.GET_USER_MENAGEMENT(),
      {
        email: params.email,
        role: params.role,
      },
    );
    return res.data;
  },
  updateUserManagment: async (params: { role: number; id: number }) => {
    const res = await getAxiosConfig().put<IUserManagement<GenericTypesUserManagement[]>>(
      ENDPOINTS.UPDATE_USER_MENAGEMENT(params.id),
      {
        role: params.role,
      },
    );
    return res.data;
  },
  deleteUserManagment: async (params: { id: number }) => {
    const res = await getAxiosConfig().delete<IUserManagement<GenericTypesUserManagement[]>>(
      ENDPOINTS.DELETE_USER_MENAGEMENT(params.id),
    );
    return res.data;
  },

  retryPayment: async (pathname: string) => {
    const res = await getAxiosConfig().post(ENDPOINTS.RETRY_PAYMENT(), { pathname });
    return res.data;
  },

  getChurnkeyTokens: async () => {
    const res = await getAxiosConfig().get<WithData<IChurnkeyTokenData>>(
      ENDPOINTS.GET_USER_CHURNKEY(),
    );
    return res.data;
  },
};

const userGenerateDashboard = {
  postGenerateStore: async (params: any, type: string, formData: boolean) => {
    const res = await getAxiosConfig().post<any>(`/qr-code/${type}`, params, {
      headers: {
        "Content-Type": !formData ? "application/json" : "multipart/form-data",
      },
    });
    return res.data;
  },

  postQRStore: async (params: Record<string, any>, type: string) => {
    const res = await getAxiosConfig().post<any>(`/qr-code/${type}`, params, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.data;
  },
  postGenerateConfigUpdate: async (params: any, url: string, formData: boolean) => {
    const res = await getAxiosConfig().post<any>(url, params, {
      headers: {
        "Content-Type": !formData ? "application/json" : "multipart/form-data",
      },
    });
    return res.data;
  },
  putGenerateStore: async (id: number, data: any) => {
    const res = await getAxiosConfig().put(`/qr-code/${id}`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.data;
  },

  updateQrConfig: async (id: number, data: any) => {
    const res = await getAxiosConfig().patch(`/qr-code/${id}?config_only=1`, data, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.data;
  },
  updateQrName: async (id: number, data: { name: string }) => {
    const res = await getAxiosConfig().patch<Data<IResponseFromQrApi<Record<string, any>>>>(
      `/qr-code/${id}?name_only=1`,
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return res.data;
  },
  resetQrConfig: async (url: string) => {
    const res = await getAxiosConfig().patch(`${url}&config_reset=1`);
    return res.data;
  },
  updateGenerateFromEdit: async (params: Record<string, any>, url: string, formData: boolean) => {
    const res = await getAxiosConfig().post<any>(url, params, {
      headers: {
        "Content-Type": !formData ? "application/json" : "multipart/form-data",
      },
    });
    return res.data;
  },
  postQrStore: async (params: any, type: string, formData: boolean) => {
    const res = await getAxiosConfig().post<any>(`/qr-code/${type}`, params, {
      headers: {
        "Content-Type": !formData ? "application/json" : "multipart/form-data",
        // "X-Download": fileType ?? "png",
      },
    });
    return res.data;
  },
};

export { user, userGenerateDashboard };
